import { z } from 'zod';

/**
 * App credential contracts (ADR-017, 2026-06-16). A per-build mobile
 * credential is a VERSION GATE, not a data-access secret: it grants no
 * data, it only decides whether a build may talk to the API at all.
 */

export const AppPlatformSchema = z.enum(['IOS', 'ANDROID']);
export type AppPlatform = z.infer<typeof AppPlatformSchema>;

export const AppCredentialStatusSchema = z.enum(['ACTIVE', 'DEPRECATED']);
export type AppCredentialStatus = z.infer<typeof AppCredentialStatusSchema>;

export const AppCredentialDtoSchema = z.object({
  id: z.string().uuid(),
  platform: AppPlatformSchema,
  version: z.string(),
  // Full plaintext value. The credential is a version gate, not a
  // data-access secret, so the SUPER_ADMIN list/create may surface it.
  // Never logged/audited in plaintext.
  credential: z.string(),
  status: AppCredentialStatusSchema,
  label: z.string().nullable(),
  createdAt: z.string(),
  deprecatedAt: z.string().nullable(),
  createdBy: z
    .object({
      id: z.string().uuid(),
      fullName: z.string(),
      cod: z.string().nullable(),
    })
    .nullable(),
});
export type AppCredentialDto = z.infer<typeof AppCredentialDtoSchema>;

export const CreateAppCredentialInputSchema = z
  .object({
    platform: AppPlatformSchema,
    version: z.string().trim().min(1).max(32),
    label: z.string().trim().max(120).optional(),
  })
  .strict();
export type CreateAppCredentialInput = z.infer<typeof CreateAppCredentialInputSchema>;

export const UpdateAppCredentialInputSchema = z
  .object({ status: AppCredentialStatusSchema })
  .strict();
export type UpdateAppCredentialInput = z.infer<typeof UpdateAppCredentialInputSchema>;

export const AppStatusReasonSchema = z.enum([
  'OK',
  'MISSING',
  'UNKNOWN',
  'MISMATCH',
  'DEPRECATED',
]);
export type AppStatusReason = z.infer<typeof AppStatusReasonSchema>;

export const AppStatusResponseSchema = z.object({
  supported: z.boolean(),
  reason: AppStatusReasonSchema,
  latestVersion: z.string(),
  minSupported: z.string(),
  updateUrl: z.string().nullable(),
});
export type AppStatusResponse = z.infer<typeof AppStatusResponseSchema>;

export const AppCredentialListResponseSchema = z.object({
  items: z.array(AppCredentialDtoSchema),
});
export type AppCredentialListResponse = z.infer<typeof AppCredentialListResponseSchema>;
